import { useStore } from 'vuex';
import { Status, UserResponseDto } from '@clearbanc/baus-sdk';
import { computed, Ref, ref, watch } from 'vue';
import { useToast } from './useToast';
import * as Sentry from '@sentry/vue';
import { LocalStorage } from '@/utils/local-storage';
import { RESEND_AUTH0_INVITE_COOLDOWN_MINUTES } from '@/data/user-invite';

/**
 * Composable for user-related operations and checks, scoped to a specific user ID.
 * @param userIdRef - A Ref or ComputedRef containing the user ID to operate on.
 */
export function useUserInviteManagement(userIdRef?: Ref<string | undefined>) {
  const store = useStore();
  const toast = useToast();

  // --- Computed Store Getters ---

  // Current user being acted upon
  const currentUser = computed<UserResponseDto | undefined>(() => {
    if (!userIdRef?.value) return undefined;
    const users: UserResponseDto[] = store.getters.allUsers || [];
    return users.find((user) => user.id === userIdRef.value);
  });

  // Reactive state for cooldown
  const inviteCooldownActive = ref<boolean>(false);

  const loadingUserInvite = ref(false);

  const hasNoInvite = computed(() => {
    return currentUser.value?.status === Status.Created;
  });

  const hasPendingInvite = computed(() => {
    return (
      currentUser.value?.status === Status.Invited ||
      currentUser.value?.status === Status.InviteExpired
    );
  });

  const canResendInvite = computed(() => {
    return (
      currentUser.value?.status === Status.InviteExpired ||
      (hasPendingInvite.value && !inviteCooldownActive.value)
    );
  });

  const hasExpiredInvite = computed(() => {
    return currentUser.value?.status === Status.InviteExpired;
  });

  async function sendInvite(user: UserResponseDto): Promise<void> {
    loadingUserInvite.value = true;
    try {
      const userInviteCooldownKey = LocalStorage.getInviteResendCooldownKey(
        user.id,
      );
      LocalStorage.setItem(
        userInviteCooldownKey,
        true,
        RESEND_AUTH0_INVITE_COOLDOWN_MINUTES,
      );
      inviteCooldownActive.value = true;

      await store.dispatchApiAction('INVITE_USER', {
        businessId: user.businessId,
        inviteExistingUserDto: {
          userId: user.id,
        },
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'sendInvite',
          userId: user.id,
        },
      });
    }
    loadingUserInvite.value = false;
  }

  async function resendInvite(): Promise<void> {
    const user = currentUser.value;

    if (canResendInvite.value && user) {
      await sendInvite(user);
      toast.success(`Resent invite to ${currentUser.value.fullName}`);
    }
  }

  // Watcher to set the resend invite cooldown state
  // when the user ID changes
  watch(
    () => currentUser.value?.id,
    (userId) => {
      if (userId) {
        const userInviteCooldownKey =
          LocalStorage.getInviteResendCooldownKey(userId);
        inviteCooldownActive.value = !!LocalStorage.getItem(
          userInviteCooldownKey,
        );
      } else {
        inviteCooldownActive.value = false;
      }
    },
    { immediate: true },
  );

  return {
    hasNoInvite,
    hasExpiredInvite,
    hasPendingInvite,
    canResendInvite,
    loadingUserInvite,
    sendInvite,
    resendInvite,
  };
}
